import { ComponentProps } from 'react';
import { Box, Chip, List, ListItem, styled } from '@mui/material';
import { Match } from 'effect';

import { formatFlag } from '../../helpers/FormattingHelpers';
import type { Flag, FlagStatus } from '../../types/Flag';
import type { LoggerMasterStatus } from '../../types/Logger';
import LoggerStatusJsx from '../LoggerCheckStatus/LoggerCheckStatus';

const FlagList = styled(List)`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;
    padding: 0;
`;

const FlagListItem = styled(ListItem)`
    padding: 0;
    width: auto;
`;

type FlagChipsProps = {
    status: LoggerMasterStatus | undefined;
    message: string | undefined;
    flags: Array<Flag>;
};

export const FlagChips = ({ status, message, flags }: FlagChipsProps) => {
    const sortedFlags = structuredClone(flags).sort(sortFlagsBySeverity);

    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap="2px">
            <LoggerStatusJsx message={message} status={status} />

            <FlagList aria-label="Flags">
                {sortedFlags.map((flag, index) => (
                    <FlagListItem key={index}>
                        <Chip
                            label={formatFlag(flag)}
                            color={matchFlagStatusToColor(flag.status)}
                            variant="outlined"
                            sx={{ height: '22px' }}
                        />
                    </FlagListItem>
                ))}
            </FlagList>
        </Box>
    );
};

const sortFlagsBySeverity = (a: Flag, b: Flag): number => b.severity - a.severity;

type ChipColor = ComponentProps<typeof Chip>['color'];

const matchFlagStatusToColor = (flagStatus: FlagStatus): ChipColor => {
    return Match.type<FlagStatus>().pipe(
        Match.when('passed', () => 'success' as const),
        Match.when('inspect', () => 'warning' as const),
        Match.when('failed', () => 'error' as const),
        Match.exhaustive
    )(flagStatus);
};
